import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

export interface ProviderBalanceCurrency {
  id: string;
  code: string;
  symbol: string;
}

export interface ProviderBalanceOverviewRow {
  api_provider: {
    id: string;
    code: string;
    name: string;
    supports_balance_api: boolean;
  };
  financial_entity: { code: string; name: string } | null;
  market_currency_code?: string | null;
  currency_code: string | null;
  currency_symbol: string | null;
  account_balance: string;
  calculated_balance: string;
  reconciliation_balance: string;
  calculated_vs_ledger: string;
  reconciliation_vs_ledger: string;
  latest_reconciliation: Record<string, unknown> | null;
}

export interface ProviderBalanceHubData {
  api_provider: {
    id: string;
    code: string;
    name: string;
    supports_balance_api: boolean;
  };
  financial_entity: { id: string; code: string; name: string } | null;
  currencies: ProviderBalanceCurrency[];
  currency: ProviderBalanceCurrency | null;
  ledger_account: { id: string; code: string; name: string } | null;
  balances: {
    account_balance: string;
    calculated_balance: string;
    reconciliation_balance: string;
    calculated_vs_ledger: string;
    reconciliation_vs_ledger: string;
  };
  breakdown_all_time: Record<string, string>;
  breakdown_since_reconciliation: Record<string, string>;
  latest_reconciliation: Record<string, unknown> | null;
  reconciliation_history: Record<string, unknown>[];
}

@Injectable({ providedIn: 'root' })
export class ProviderBalanceService {
  private readonly api = inject(ApiService);

  listOverview(): Observable<ProviderBalanceOverviewRow[]> {
    return this.api.get<ProviderBalanceOverviewRow[]>('finances/provider-balances/');
  }

  getHub(
    apiProviderId: string,
    options?: { currencyCode?: string; marketCurrencyCode?: string }
  ): Observable<ProviderBalanceHubData> {
    const params: Record<string, string> = {};
    if (options?.marketCurrencyCode) {
      params['market_currency_code'] = options.marketCurrencyCode;
    }
    if (options?.currencyCode) {
      params['currency'] = options.currencyCode;
    }
    return this.api.get<ProviderBalanceHubData>(
      `finances/provider-balances/${apiProviderId}/hub/`,
      Object.keys(params).length ? params : undefined
    );
  }

  reconcile(
    apiProviderId: string,
    body: {
      provider_reported_balance: string;
      currency?: string;
      snapshot_at?: string;
      notes?: string;
    }
  ): Observable<Record<string, unknown>> {
    return this.api.post<Record<string, unknown>>(`finances/provider-balances/${apiProviderId}/reconcile/`, body);
  }

  fetchBalance(apiProviderId: string, currencyCode: string, notes = ''): Observable<Record<string, unknown>> {
    return this.api.post<Record<string, unknown>>(`finances/provider-balances/${apiProviderId}/fetch-balance/`, {
      notes,
      currency: currencyCode,
    });
  }

  createTopup(body: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.api.post<Record<string, unknown>>('finances/provider-balance-topups/', body);
  }
}
